import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { staffOrdersTourId, staffOrdersSteps } from '@/tours/staff';
import { Package } from 'lucide-react';
import { Card, EmptyState, Pagination, SearchInput, Select, LoadingSpinner, ErrorMessage } from '@/components/ui';
import { OrdersTable } from '@/components/features/orders';
import { ordersApi } from '@/services/api/ordersApi';
import { queryKeys } from '@/services/api/queryClient';
import { OrderStatus, OrderStatusLabels } from '@/types/enums';

const ITEMS_PER_PAGE = 20;

const statusOptions = [
  { value: '', label: 'Все статусы' },
  ...Object.entries(OrderStatusLabels).map(([value, label]) => ({ value, label })),
];

const OrdersPage = () => {
  useTourAutoStart({ tourId: staffOrdersTourId, steps: staffOrdersSteps });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const filtersKey = JSON.stringify({ search, status: statusFilter, page });

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.orderList(filtersKey),
    queryFn: () =>
      ordersApi.getList({
        search: search || undefined,
        status: (statusFilter as OrderStatus) || undefined,
        page,
        limit: ITEMS_PER_PAGE,
      }),
  });

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1);
  }, []);

  const orders = data?.data ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Заказы</h1>
        <p className="text-sm text-gray-500 mt-1">
          Управление заказами пункта выдачи
          {totalCount > 0 && <span className="text-gray-400"> • {totalCount} всего</span>}
        </p>
      </div>

      <Card>
        <Card.Header>
          <div className="flex flex-col sm:flex-row gap-3">
            <div data-tour="staff:orders-search" className="flex-1">
              <SearchInput
                value={search}
                onChange={handleSearchChange}
                placeholder="Поиск по номеру, накладной, ФИО клиента..."
                className="w-full"
              />
            </div>
            <div data-tour="staff:orders-filters">
              <Select
                options={statusOptions}
                value={statusFilter}
                onChange={handleStatusChange}
                className="sm:w-48"
              />
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          <div data-tour="staff:orders-table">
          {isLoading ? (
            <LoadingSpinner size="lg" className="py-16" />
          ) : error ? (
            <ErrorMessage message="Не удалось загрузить заказы" className="py-12" />
          ) : orders.length === 0 ? (
            <EmptyState
              icon={<Package className="w-8 h-8" />}
              title="Заказов не найдено"
              description={search || statusFilter ? 'Попробуйте изменить параметры поиска' : 'Загрузите поставку чтобы начать'}
            />
          ) : (
            <OrdersTable orders={orders} />
          )}
          </div>
        </Card.Body>

        {totalPages > 1 && (
          <Card.Footer>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </Card.Footer>
        )}
      </Card>
    </div>
  );
};

export default OrdersPage;
